'use client';
import React from 'react';

import Box from '@mui/material/Box';
import { usePathname } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { useLocale } from 'next-intl';
import styled from 'styled-components';

import Item from './Item';
import { HeadingSizeType } from '@/ui/components/Heading';
import Heading from '@/ui/components/Heading';
import { log } from '@/utils/logger';
import { urlBuilder } from '@/utils/urlBuilder';

const List = styled.ul`
  border-bottom: 1px #e0e0e0 solid;
  display: block;
  margin: 0;
  padding: 0;

  ul {
    border-bottom: 0;

    li {
      padding-left: 10px;
    }
  }

  li {
    border-top: 1px #e0e0e0 solid;
    display: block;
    margin: 0;
  }

  a {
    color: ${({ theme }) => theme.palette.text.primary};
    display: block;
    text-decoration: none;

    &:hover {
      span {
        text-decoration: underline;
      }
    }
  }

  strong {
    color: ${({ theme }) => theme.custom.colors.text};
  }
`;

const ListItem = styled.li<{ $level: number }>`
  padding-left: ${({ $level }) => $level * 10}px;
`;

type SideMenuItem = {
  title: string;
  description?: string;
  slug?: string;
  hashAnchor?: string;
  children?: SideMenuItem[];
};

interface SideMenuProps {
  items: SideMenuItem[];
  heading: string;
  headingSize?: HeadingSizeType;
  headingComponent?: HeadingSizeType;
  upperCaseHeading?: boolean;
  visibleVersion?: string;
}

export default function SideMenu({
  items,
  heading,
  headingSize = 'h3',
  headingComponent = 'h4',
  upperCaseHeading = false,
  visibleVersion,
}: SideMenuProps) {
  const translate = useTranslations();
  const locale = useLocale();
  const pathname = usePathname();

  const renderListItems = (items, visibleVersion, level = 0, parentSlug = null) => {
    // Recursive helper to check if any child item (or nested child) has a URL matching the current pathname.
    const hasActiveChild = childItems => {
      if (!childItems) return false;
      return childItems.some(child => {
        if (pathname.includes(child?.slug)) {
          return true;
        }
        return child.childrens ? hasActiveChild(child.childrens) : false;
      });
    };

    return (
      <List>
        {items.map(item => {
          // Create a new item with default type and parentSlug if not provided.
          const newItem = {
            ...item,
            type: item.type || 'topic',
            parentSlug: item.parentSlug || parentSlug,
          };

          return (
            <ListItem $level={level} key={`${newItem.hashAnchor || newItem?.slug}-${level}`}>
              <Item item={newItem} active={!!newItem.hashAnchor && visibleVersion === newItem.hashAnchor} />
              {(pathname === urlBuilder({ item: newItem, locale, silent: true }) ||
                (newItem.childrens && hasActiveChild(newItem.childrens))) &&
                newItem.childrens &&
                renderListItems(newItem.childrens, visibleVersion, level + 1, newItem.parentSlug)}
            </ListItem>
          );
        })}
      </List>
    );
  };

  if (!items?.length) {
    log.warning('No items to display in SideMenu');
    return null;
  }

  return (
    <Box>
      <Heading
        headingSize={headingSize}
        headingComponent={headingComponent}
        sx={{
          textTransform: upperCaseHeading ? 'uppercase' : 'none',
          marginBottom: upperCaseHeading ? '0.3rem' : '1.25rem',
          marginTop: 0,
        }}
      >
        {translate(`sidemenu-heading-${heading?.toLowerCase()}`)}
      </Heading>
      <Box>{renderListItems(items, visibleVersion)}</Box>
    </Box>

    /* <li>
      <a href="">
        <strong>Material Filaments Guide</strong>
        <br />
        Table including all sorts of supportedfilaments
      </a>
    </li>
    <li>
      <a href="">
        <strong>Printable parts</strong>
        <br />
        Modify and upgrade your MK4
      </a>
    </li>
    <li>
      <a href="">
        <strong>Spare parts</strong>
        <br />
        Buy MK4 spare parts online
      </a>
    </li>
    <li>
      <a href="">
        <strong>360° printer viewer</strong>
        <br />
        Check out MK4 3D model in browser
      </a>
    </li>
    <li>
      <a href="">
        <strong>G-code samples</strong>
        <br />
        Download MK4 print examples
      </a>
    </li>
    <li>
      <a href="">
        <strong>Read more on E-shop</strong>
        <br />
        More about this 3D printer on our e-shop
      </a>
    </li>
  </Links>

  <Typography variant="h3" component="h4">
    Topics
  </Typography>
  <Links2>
    {topics?.map(li => (
      <li key={li.id}>
        <a href={li?.slug}>{li.title}</a>
      </li>
    ))}
  </Links2> */
  );
}
